import { Injectable } from '@angular/core';
import { DateTime } from 'luxon';
import { CheckoutResult } from '@cf-shared/user-subscription-interface';
import { base64UrlEncodeObject } from '@cf-shared/shared-util';
import Bowser from 'bowser';
import { CheckoutTrackingData, URL_SOURCED_TRACKING_KEYS, NON_URL_TRACKING_KEYS, buildCheckoutTrackingFields } from '@cf-shared/checkout-tracking';
import { resolveClientEnvironment } from '@cf-shared/client-environment';
import { getDeviceId, isAppStandalone, isCapacitorNative } from 'environments/environment-helpers';
import { resolveFirstSeenClickTime } from './click-time-capture';
import { loadCachedTracking, resolveClickIdsFromCache } from './tracking-cache';
import { removeUrlSearchKeys } from './url-search-keys';
import { noteGpcSeen, resolveEffectiveAdConsent } from './ad-consent-cookie';

// list of all URL query keys from the checkout result that we don't care about
const dummyCheckoutKeys = [
  'exfo', 'x-uid', 'x-data', 'page-template', 'billing-country', 'billing-email', 'currency', 'products[1][id]', 'use-test-mode', 'secret-key',
  'atc_id'
];

/**
 * The address of the page the buyer is on, with the query string and any `#`
 * fragment removed — this is what Meta calls `event_source_url` (#5043).
 *
 * The query string is dropped on purpose. Meta needs to know WHICH PAGE the
 * purchase happened on, and nothing more; the marketing parameters that live in
 * the query string already travel to Meta separately, as proper match keys and
 * telemetry fields. Sending them a second time inside a URL would copy them, and
 * whatever else a link happened to carry, into Meta's records and into our own
 * server logs for no gain.
 */
function currentPageUrlWithoutQuery(): string {
  return window.location.origin + window.location.pathname;
}

@Injectable()
export class UrlQueryService {

  private urlParams: URLSearchParams = new URLSearchParams(window.location.search);
  private referrer = document.referrer;
  private trackingData = this.generateTrackingData();

  constructor(
  ) {
    this.parseUrlSearch();
  }

  // get any of the original query parameters that were present when the app was loaded
  getUrlParam(name: string): string {
    return this.urlParams.get(name);
  }

  /**
   * Read the address bar again, as if the app had just loaded at the address it
   * shows now.
   *
   * The parameters above are captured once, when this service is built, because
   * for an ordinary page load the address never changes underneath it. The
   * cold-start deep-link path breaks that assumption on purpose: it writes the
   * launch link's path and query into the address bar and then needs everything
   * that reads a parameter to see them. See `ExternalFlowService.adoptBootParamsInPlace`.
   *
   * The tracking data is re-derived too, and that is not tidiness. Until this
   * existed, a cold-start link was read by RELOADING, and the fresh page built
   * this service over the new address — so a link carrying campaign parameters
   * contributed them. Re-reading the parameters without re-deriving the tracking
   * data would have dropped them silently, which is the kind of loss nobody
   * notices for months.
   *
   * Only for a boot that has not signed anybody in yet. After a login the server's
   * stored tracking data is the authority, and re-deriving from a URL would
   * overwrite it with less.
   */
  rereadAddressBar() {
    this.urlParams = new URLSearchParams(window.location.search);
    this.trackingData = this.generateTrackingData();
    this.parseUrlSearch();
  }

  getAllUrlParamsAsObject(): any {
    let params = {} as any;
    if (!this.urlParams || typeof this.urlParams['forEach'] !== 'function') {
      return params;
    }
    this.urlParams['forEach']((value, key) => {
      params[key] = value;
    });
    return params;
  }

  getTrackingData(): any {
    return this.trackingData || {};
  }

  /**
   * The tracking fields a checkout carries back to the server, as a plain
   * object. We default `utm_clid` to the device id so the server can correlate
   * the buyer with their initial session. Built on a shallow copy so the shared
   * tracking singleton is never mutated.
   *
   * Two callers, one rule: `getTrackingDataBase64` base64-encodes this for the
   * checkout endpoints, and the Paddle pages stamp it into the checkout's
   * `custom_data` so the webhook receives it too (#4045 / #4257). Paddle keeps
   * `custom_data` on the subscription and echoes it on every later webhook, so
   * this is also how the click id reaches the day-14 conversion event.
   *
   * `clientIp` is the buyer's own address, which only the SERVER can see (the
   * browser cannot read it) — the Paddle checkout config carries it back for
   * this purpose. Pass it on the Paddle path so the webhook can geo-locate the
   * telemetry and hand Meta a match key, the way PayPro already does with its
   * IPN's `CUSTOMER_IP`. Omitted elsewhere.
   *
   * The device of purchase (#4096) is added HERE and not in
   * `generateTrackingData`, for the same reason `client_ip` is: that bag is
   * shared with `TelemetryService`, which spreads it onto every client event AND
   * onto the Mixpanel people profile. These two describe one checkout, so a
   * profile copy would be rewritten on every app start and would silently
   * rewrite a person's own past — the very thing #4096 exists to stop.
   *
   * The checkout page's address (#5043) is added here for that same reason, and
   * is what Meta requires as `event_source_url` before it will use a conversion
   * for attribution. Only the browser knows which page the buyer is actually on.
   *
   * `serverSawGpc` is the one thing the browser cannot see for itself: whether
   * our own server read `Sec-GPC: 1` on the request that built this checkout
   * (#5663). Some browsers and extensions set only the header and never the
   * page-visible property, and without this their purchase would be reported.
   */
  getTrackingDataForCheckout(clientIp?: string, serverSawGpc = false): CheckoutTrackingData {
    // which device this checkout is happening on. Resolved in the browser
    // because it is the only place that knows both whether the app shell is
    // running and which operating system it is on — see
    // shared/src/client-environment.ts.
    const clientEnvironment = resolveClientEnvironment({
      isNative: isCapacitorNative(),
      isStandalone: isAppStandalone(),
      osName: Bowser.getParser(navigator.userAgent).getResult().os.name,
    });
    // if this visit carried no click id, fall back to the one the browser
    // remembers from an earlier visit. Plenty of people click an ad, leave, and
    // come back days later by bookmark, and their purchase used to reach Meta
    // with nothing to match it to the ad that produced it — measured on
    // 2026-09-09 at 43% of ad-driven web subscribers.
    //
    // Done HERE, on a copy, for the same reason the device and IP fields above
    // are: `getTrackingData()` returns the bag SHARED with TelemetryService, and
    // writing a restored click id into it would flip that service's
    // "fresh campaign visit" test, making it overwrite the remembered campaign
    // fields with just the click ids — and would also put the click id into
    // outbound links, where it would come back stamped with the wrong click
    // time. A click id in the URL always wins, and `utm_source` is never
    // restored; see resolveClickIdsFromCache for both rules.
    const urlTracking = this.getTrackingData();
    const withRememberedClickIds = {
      ...urlTracking,
      ...resolveClickIdsFromCache(urlTracking, loadCachedTracking()),
    };
    return buildCheckoutTrackingFields(withRememberedClickIds, {
      deviceId: getDeviceId(),
      clientIp,
      pageUrl: currentPageUrlWithoutQuery(),
      clientEnvironment,
      // this browser's Advertising answer (#5587), with the Global Privacy
      // Control signal applied (#5663). Read now, at checkout, so an answer given
      // a moment ago is the one sent. The server decides from it whether this
      // purchase may be reported to Meta and Google Ads.
      adConsent: resolveEffectiveAdConsent(serverSawGpc),
      // the stamp the answer above was judged against (#5663). Its PRESENCE is
      // what tells the server this browser had a signal in view when it decided,
      // which is the only thing that lets a "yes" survive a `Sec-GPC` header the
      // server sees on the same request.
      gpcSince: noteGpcSeen(serverSawGpc),
    });
  }

  /**
   * The base64 telemetry blob the checkout endpoints read (`storeGetCheckout`
   * for personal, `coachStartPaddleCheckout` for coaching). Its `utm_source`
   * `e2e-test` marker is what the server uses to pick the Paddle sandbox vs
   * live environment.
   */
  getTrackingDataBase64(): string {
    return base64UrlEncodeObject(this.getTrackingDataForCheckout());
  }

  /**
   * The base64 telemetry blob `couchLogin` reads so it can record where this
   * person came from on the ONE-TIME create of their user document (#5408).
   *
   * Why it is not `getTrackingDataForCheckout()`: that bag carries per-checkout
   * facts — the device id, the buyer's IP, the checkout page's address, the
   * device of purchase — which describe one purchase and have no meaning on an
   * account. The server drops them anyway.
   *
   * Why it is not `getTrackingData()` either: the plain bag holds only what THIS
   * visit's URL carried, so someone who clicked an ad days ago and came back by
   * bookmark would sign up with no click id at all. Measured on 2026-09-09, that
   * is 43% of ad-driven web subscribers. So the remembered click ids are merged
   * in the same way the checkout merges them.
   *
   * Built on a COPY, for the reason spelled out in `getTrackingDataForCheckout`:
   * `getTrackingData()` returns the bag SHARED with TelemetryService, and writing
   * a restored click id into it would flip that service's "fresh campaign visit"
   * test and put the click id into outbound links.
   */
  getTrackingDataForAccountCreationBase64(): string {
    const urlTracking = this.getTrackingData();
    return base64UrlEncodeObject({
      ...urlTracking,
      ...resolveClickIdsFromCache(urlTracking, loadCachedTracking()),
    });
  }

  generateTrackingData() {
    let data = {} as any;

    // get all known UTM fields, and any other tracking fields that are present in
    // the URL. The list is shared with the server, which reads these same keys back
    // off the checkout — see shared/src/checkout-tracking.ts.
    let utmFields = URL_SOURCED_TRACKING_KEYS;
    for (let field of utmFields) {
      let value = this.urlParams.get(field);
      if (value) {
        data[field] = value;
      }
    }

    if (this.referrer) {
      let domain = new URL(this.referrer).hostname;
      if (domain.endsWith('mh-physique.app')) {
        // self-referral (e.g. after location.reload()) — use stored original if available
        let stored = sessionStorage.getItem('tracking_referrer');
        if (stored) {
          data['$referrer'] = stored;
          data['$referrer_domain'] = sessionStorage.getItem('tracking_referrer_domain');
        } else {
          data['$referrer'] = 'direct';
        }
      } else {
        // real external referrer — use and persist for reloads
        data['$referrer'] = this.referrer;
        data['$referrer_domain'] = domain;
        sessionStorage.setItem('tracking_referrer', this.referrer);
        sessionStorage.setItem('tracking_referrer_domain', domain);
      }
    } else {
      data['$referrer'] = 'direct';
    }

    // capture browser user-agent for Meta CAPI event match quality scoring
    data['client_user_agent'] = navigator.userAgent;

    // remember WHEN the Facebook click id was first seen. Meta's `fbc` value is
    // `fb.1.<click_time_ms>.<fbclid>` and wants the time of the click itself, not
    // the time we later report the purchase — and the trial-to-paid event fires
    // about 14 days after the click, so "now" would be two weeks wrong there.
    if (data['fbclid']) {
      data['fbclid_ts'] = this.rememberFirstSeenClickTime(data['fbclid']);
    }

    return data;
  }

  /**
   * The epoch-millis time we FIRST saw this Facebook click id, as a string.
   *
   * The click id stays in the URL across reloads, so re-stamping "now" on every
   * page load would keep pushing the recorded click time later. We store the
   * first value against the click id itself, so a reload returns the original
   * time while a genuinely new ad click (a different click id) starts fresh.
   * sessionStorage, matching how the referrer above is preserved across reloads.
   */
  private rememberFirstSeenClickTime(fbclid: string): string {
    return resolveFirstSeenClickTime(fbclid, sessionStorage, DateTime.now().toMillis());
  }

  // this will return string in format "key1=value1&key2=value2&key3=value3"
  // excludes fields that are only relevant for server-side conversion tracking (not for URL params)
  getTrackingDataAsUrlParams() {
    let data = this.getTrackingData();
    let params = new URLSearchParams();
    for (let key in data) {
      // skip the fields that only ever matter to server-side conversion tracking —
      // putting them in a link would bloat OTL emails and external URLs for nothing,
      // and nothing reads them back out of a URL. See NON_URL_TRACKING_KEYS.
      if ((NON_URL_TRACKING_KEYS as readonly string[]).includes(key)) continue;
      params.set(key, data[key]);
    }
    return params.toString();
  }

  parseUrlSearch() {

    // handle checkout result
    let checkoutResult = this.urlParams.get('checkout_result');
    if (!checkoutResult) {
      // nothing to do
    } else if (
      checkoutResult == CheckoutResult.Fulfilled ||
      checkoutResult == CheckoutResult.Declined ||
      checkoutResult == CheckoutResult.Review ||
      checkoutResult == CheckoutResult.Waiting) {
      console.log(`UrlQuery: Checkout result "${checkoutResult}"`);
      // remove the keys that we will never care about
      this.clearUrlSearchKeys(dummyCheckoutKeys);
    } else {
      checkoutResult = undefined;
      console.error(`UrlQuery: Invalid checkout result "${checkoutResult}"`);
    }
  }

  // only clear if we have already used any eventual parsed data (we clear the data when used)
  // otherwise, we keep this in url, so it survives refreshes, etc - until used
  clearUrlSearchKeys(keys: string[]) {
    this.forgetUrlParams(keys);

    // Build the new address from what the address bar says RIGHT NOW, not from the
    // boot-time copy above. The two normally hold the same thing, but anything that
    // rewrote the address after boot would otherwise be undone here — which is how the
    // community sign-in credential could get written back after being cleared (#5151).
    const urlParser = new URL(window.location.href);
    urlParser.search = removeUrlSearchKeys(window.location.search, keys);
    // this is a hack to clear query string out of the url without reloading the page
    history.replaceState({}, '', urlParser.href);
  }

  /**
   * Drop parameters from the boot-time copy of the query WITHOUT touching the address bar.
   *
   * For a caller that has already rewritten the address itself and only needs this service
   * to stop reporting the parameter. `getAllUrlParamsAsObject` ships this copy whole as
   * event properties, so a credential left in it would travel even once the address is
   * clean (#5151).
   */
  forgetUrlParams(keys: string[]) {
    // the page trail must survive a batch clear that happens to name it
    const breadcrumbsParam = this.urlParams.get('breadcrumbs');

    for (const key of keys) {
      this.urlParams.delete(key);
    }

    if (breadcrumbsParam !== null) {
      this.urlParams.set('breadcrumbs', breadcrumbsParam);
    }
  }
}
